import { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import TransferCategoryCard from "./TransferCategoryCard";

type TransferCategory = {
  name: string;
  description: string;
};

type Agent = {
  id: string;
  topic?: string;
  config?: {
    agentName?: string;
  };
};

type TransferCategoriesTabProps = {
  agents: Agent[];
  isLoadingAgents: boolean;
  currentCategories: TransferCategory[] | undefined;
  isLoadingCategories: boolean;
  selectedAgentId: string;
  onAgentChange: (agentId: string) => void;
  onSaveCategories: (categories: TransferCategory[]) => void;
  isSaving: boolean;
};

export const TransferCategoriesTab = ({
  agents,
  isLoadingAgents,
  currentCategories,
  isLoadingCategories,
  selectedAgentId,
  onAgentChange,
  onSaveCategories,
  isSaving,
}: TransferCategoriesTabProps) => {
  // State for categories
  const [categories, setCategories] = useState<TransferCategory[]>([]);

  // Load current categories when they are fetched
  useEffect(() => {
    if (currentCategories) {
      setCategories(
        currentCategories.map((cat) => ({
          name: cat.name,
          description: cat.description,
        }))
      );
    } else {
      setCategories([]);
    }
  }, [currentCategories]);

  const handleAddCategory = useCallback(() => {
    setCategories((prev) => [
      ...prev,
      {
        name: "",
        description: "",
      },
    ]);
  }, []);

  const handleRemoveCategory = useCallback((index: number) => {
    setCategories((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCategoryChange = useCallback(
    (index: number, field: keyof TransferCategory, value: string | undefined) => {
      setCategories((prev) => {
        const newCategories = [...prev];
        newCategories[index] = {
          ...newCategories[index],
          [field]: value,
        };
        return newCategories;
      });
    },
    []
  );

  const handleSave = () => {
    // Validate that all required fields are filled
    const hasEmptyFields = categories.some((cat) => !cat.name.trim() || !cat.description.trim());

    if (hasEmptyFields) {
      toast.error("Error", {
        description: "Por favor complete el nombre y descripcion de todas las categorias",
      });
      return;
    }

    onSaveCategories(categories);
  };

  if (isLoadingAgents) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!agents || agents.length === 0) {
    return (
      <Alert status="warning">
        <div>
          <p className="font-semibold">No hay agentes configurados</p>
          <p className="text-sm">
            Debe crear un agente antes de configurar las categorias de transferencia.
          </p>
        </div>
      </Alert>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-6">
        <Alert status="info" className="rounded-md">
          <div>
            <p className="font-semibold">¿Que son las categorias de transferencia?</p>
            <p className="text-sm">
              Las categorias de transferencia permiten clasificar las conversaciones cuando el
              agente las transfiere a un humano.
            </p>
          </div>
        </Alert>

        {/* Agent Selector */}
        <div>
          <label className="mb-1 block text-sm font-medium">Seleccionar Agente</label>
          <select
            value={selectedAgentId}
            onChange={(e) => onAgentChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">Seleccione un agente</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.config?.agentName || agent.topic || agent.id}
              </option>
            ))}
          </select>
        </div>

        {!selectedAgentId ? (
          <div className="py-8 text-center">
            <p className="text-gray-500">Seleccione un agente para configurar sus categorias.</p>
          </div>
        ) : isLoadingCategories ? (
          <div className="flex h-[200px] items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Categories Cards */}
            <div className="flex flex-col gap-4">
              {categories.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-500">
                    No hay categorias configuradas. Haga clic en "Agregar Categoria" para comenzar.
                  </p>
                </div>
              ) : (
                categories.map((cat, index) => (
                  <TransferCategoryCard
                    key={index}
                    category={cat}
                    onCategoryChange={(field, value) => handleCategoryChange(index, field, value)}
                    onRemove={() => handleRemoveCategory(index)}
                  />
                ))
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col justify-between gap-3 md:flex-row">
              <Button
                variant="outline"
                onClick={handleAddCategory}
                className="min-h-11 w-full md:w-auto"
              >
                <Plus className="mr-2 size-4" />
                Agregar Categoria
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="min-h-11 w-full md:w-auto"
              >
                {isSaving ? "Guardando..." : "Guardar Configuracion"}
              </Button>
            </div>

            {/* Examples Section */}
            <div className="mt-4 rounded-md bg-gray-50 p-4">
              <p className="mb-2 font-semibold">
                Ejemplos de categorias de transferencia:
              </p>
              <div className="flex flex-col gap-2">
                <div>
                  <p className="text-sm font-medium">
                    Urgente:
                  </p>
                  <p className="text-sm text-gray-600">
                    Cliente listo para cerrar negocio o con necesidad inmediata.
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    Seguimiento:
                  </p>
                  <p className="text-sm text-gray-600">
                    Cliente interesado pero necesita mas informacion o tiempo.
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    Consulta General:
                  </p>
                  <p className="text-sm text-gray-600">
                    Preguntas generales sobre servicios o procesos.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
